<script>
  import { computed, onMounted, useRouter } from '@nuxtjs/composition-api'
  import { productGetters } from '@/composables/propeller/getters'
  import Badge from '@/components/Ecommerce/Badge'
  import BreadCrumbs from '@/components/Ecommerce/BreadCrumbs'
  import ProductPrice from '@/components/Ecommerce/Products/ProductPrice'
  import ProductStock from '@/components/Ecommerce/Products/ProductStock'
  import ProductPartners from '@/components/Ecommerce/Products/ProductPartners'
  import ProductAddToCart from '@/components/Ecommerce/Products/ProductAddToCart'
  import ProductAttributes from '@/components/Ecommerce/Products/ProductAttributes'
  import ProductDescription from '@/components/Ecommerce/Products/ProductDescription'
  import ProductFreeShipping from '@/components/Ecommerce/Products/ProductFreeShipping'
  import ProductRelatedProducts from '@/components/Ecommerce/Products/ProductRelatedProducts'
  import { usePartners } from '@/composables/usePartners'
  import { firstAttribute, useAttribute, useListAttribute } from '@/composables/useAttribute'
  import { useModal } from '@/composables/useModal'
  import { isAvailable, isOrderable, setProductsListTracking } from '@/utils/utils'
  import { productAttributes } from '@/constants/products.module'
  import { gtmSendEvent } from '~/utils/gtm-send-event'
  import ProductImageCarousel from '@/components/Ecommerce/Products/ProductImageCarousel.vue'
  import ProductRelatedProductSmall from '@/components/Ecommerce/Products/ProductRelatedProductSmall.vue'

  export default {
    components: {
      Badge,
      BreadCrumbs,
      ProductStock,
      ProductPrice,
      ProductPartners,
      ProductAddToCart,
      ProductAttributes,
      ProductDescription,
      ProductFreeShipping,
      ProductRelatedProducts,
      ProductImageCarousel,
      ProductRelatedProductSmall,
    },
    props: {
      product: {
        type: Object,
        required: true
      }
    },
    setup (props, { root }) {
      const partners = usePartners(props.product)
      const router = useRouter()
      const { openModal } = useModal(root)

      const bundles = computed(() => productGetters.getBundleProducts(props.product))
      const hasBundle = computed(() => bundles.value.length)

      const type = computed(() => {
        const type = useAttribute(props.product, productAttributes.brand)
        return type.value ?? ''
      })
      const subject = computed(() => {
        const subject = useAttribute(props.product, productAttributes.subject)
        return subject.value ?? ''
      })

      const isExamenBuddy = computed(() => {
        const examenMerk = useAttribute(props.product, productAttributes.brand)
        return examenMerk.value === 'Examenbuddy' ?? false;
      });

      const coverImage = computed(() => {
        return !Array.isArray(props.product)
          ? productGetters.getCoverImage(props.product)
          : null
      })

      const gallery = computed(() => {
        return !Array.isArray(props.product)
          ? productGetters.getGallery(props.product)
          : []
      })
      const price = computed(() => {
        return !Array.isArray(props.product)
          ? productGetters.getPrice(props.product)
          : null
      })

      const name = computed(() => {
        return !Array.isArray(props.product)
          ? productGetters.getName(props.product)
          : null
      })

      const breadcrumbs = computed(() => {
        return [
          {
            slug: '/producten',
            title: 'producten'
          },
          {
            title: type.value + ' ' + subject.value
          }
        ]
      })

      const goBack = () => {
        !process.server
          ? window.history.back()
          : router.push('/producten')
      }

      const status = computed(() => productGetters.getStatus(props.product))

      // Inkijkexemplaar issuu URL (undefined when the product has no preview attribute).
      // useAttribute already returns a computed ref of the value, so no extra wrapping.
      const previewUrl = useAttribute(props.product, productAttributes.preview)

      const hasPreview = computed(() => !!previewUrl.value)

      const previewModal = () => {
        openModal('ProductPreview', {
          url: previewUrl.value
        })
      }
      const trackProductView = (product, productType, level) => {
        const products = setProductsListTracking([{
          ...product,
          name: productGetters.getName(product),
          productType,
          level,
        }])

        delete products[0].index
        gtmSendEvent('view_item', {
          ecommerce: {
            currency: 'EUR',
            items: products
          },
        }, null, true)
      }

      onMounted(() => {
        trackProductView(props.product, useAttribute(props.product, productAttributes.brand), useListAttribute(props.product, productAttributes.educationType).value.join(', '))
      })

      const level = computed(() => useListAttribute(props.product, productAttributes.educationType).value.join(', '))
      const isTotalPackage = computed(() => `${type.value} ${subject.value} ${level.value}`.includes('Totaalpakket') && isExamenBuddy.value)

      const relatedProducts = computed(() => productGetters.getCrossupsellProducts(props.product, ['RELATED']))

      // Voordeelpakket van hetzelfde vak — Janinka (02-09): "EB havo Duits —>
      // Voordeelpakket havo Duits". `relatedProducts[0]` leunde erop dat zij het
      // juiste vooraan zette. Geen match = geen banner.
      const bundleUpsell = computed(() => relatedProducts.value.find(({ item }) =>
        firstAttribute(item, productAttributes.brand)?.value === 'Voordeelpakket' &&
        firstAttribute(item, productAttributes.subject)?.value === subject.value
      )?.item)

      return {
        previewModal,
        hasPreview,
        goBack,
        level,
        type,
        subject,
        coverImage,
        price,
        bundles,
        attributes: computed(() => productGetters.getAttributes(props.product)),
        description: computed(() => productGetters.getDescription(props.product)),
        shortDescription: computed(() => productGetters.getShortDescription(props.product)),
        productType: useAttribute(props.product, productAttributes.brand),
        relatedProducts,
        bundleUpsell,
        status,
        partners,
        isOrderable: computed(() => isOrderable(props.product)),
        isAvailable: computed(() => isAvailable(props.product)),
        hasBundle,
        breadcrumbs,
        isExamenBuddy,
        isTotalPackage,
        gallery,
      }
    },
    head: {}
  }
</script>

<template>
  <div>
    <BreadCrumbs
      class="block md:hidden"
      :breadcrumbs="breadcrumbs"
    >
      <e-button
        type="tertiary"
        size="small"
        reversed
        @click="goBack"
      >
        terug
      </e-button>
    </BreadCrumbs>
    <div class="container my-8 lg:px-8 md:mb-30 md:mt-7">
      <BreadCrumbs
        class="hidden md:block"
        :breadcrumbs="breadcrumbs"
      >
        <e-button
          type="tertiary"
          size="small"
          reversed
          @click="goBack"
        >
          terug
        </e-button>
      </BreadCrumbs>
      <div class="product-detail-grid">
        <div class="relative items-center row-start-2 px-4 md:col-span-1 md:flex sm:row-start-1 sm:row-end-3 md:row-end-4">
          <div class="relative w-full">
            <ProductImageCarousel
              :images="gallery"
            />
            <div
              v-if="!isExamenBuddy"
              class="absolute top-2 right-2 z-10 xl:pt-30"
            >
              <button
                v-if="gallery"
                :disabled="!hasPreview"
                :class="{ 'cursor-not-allowed': !hasPreview }"
                class="flex items-center p-2 space-x-4 text-xs font-bold text-blue-500 bg-white border border-blue-500 rounded-sm preview-button"
                @click="previewModal"
              >
                <span class="flex items-center preview-icon">
                  <q-icon name="book" />
                </span>
                <span>Inkijkexemplaar</span>
              </button>
            </div>
          </div>
        </div>

        <div class="row-span-1 row-start-1 row-end-2 px-4 md:col-span-1 lg:pt-30">
          <Badge
            v-if="productType !== 'Voordeelpakket' && productType !== 'Examenbuddy'"
            small
            secondary
            rounded
            class="mb-3 md:mb-4"
          >
            Tot 15% stapelkorting
          </Badge>
          <h1
            class="text-2xl product-title"
            :title="`${type} ${subject} ${level}`"
          >
            {{ type }} <br>
            {{ subject }}
          </h1>
          <p class="mt-3 mb-5 text-lg text-gray-500 md:text-black md:font-secondary md:text-2xl md:leading-none md:mb-9">
            {{ level }}
          </p>
          <div
            class="hidden leading-relaxed md:block"
            v-html="shortDescription"
          >
          </div>
        </div>

        <div class="fixed bottom-0 left-0 right-0 z-20 row-start-3 row-end-4 bg-white border-t md:grid sm:row-start-2 sm:row-end-3 border-gray-250 md:border-none sm:relative">
          <div class="grid items-center p-4 md:p-0 md:px-4">
            <ProductPrice
              v-if="price"
              :price="price.regular"
              :discount="price.special"
              class="col-start-1 row-start-1 pr-12 text-blue-500 md:row-start-1 md:pr-0 md-col-start-auto"
              big
            />
            <div class="col-start-2 col-end-3 row-start-1 md:row-start-3 md:col-start-auto md:col-end-auto">
              <ProductStock
                class="md:mt-6"
                :is-orderable="isOrderable"
                :is-available="isAvailable"
                :is-examen-buddy="isExamenBuddy"
                is-small
              />
              <ProductFreeShipping
                :is-examen-buddy="isExamenBuddy"
                is-small
              />
            </div>
            <div class="w-full col-start-1 col-end-3 row-start-2 pt-4">
              <ProductAddToCart
                :product="product"
                :disabled="!isOrderable"
                class="flex justify-center w-full"
              >
                <span>In mijn winkelwagen</span>
              </ProductAddToCart>
            </div>
          </div>

          <ProductPartners
            class="px-4 mt-8 sm:mt-10 "
            :partners="partners"
          />
        
          <!-- <div v-if="isExamenBuddy && !isTotalPackage" class="px-4 py-4 space-y-16 max-w-148 sm:my-4 sm:space-y-4">
            <template >
              <p class="mb-6">
                <strong>Bespaar en koop het Totaalpakket</strong>
              </p>
              <ProductCTABuddy :level="level" />
            </template>
          </div>-->
        </div>
        <!-- Stond uit sinds 01-09: de banner zat op `relatedProducts[0]` en liep
             dus mee met Propeller's volgorde. -->
        <ProductRelatedProductSmall
          v-if="bundleUpsell && (type === 'Samengevat' || type === 'Examenbundel' || type === 'Examenidioom')"
          :product="bundleUpsell"
        />
      </div>
      <div
        class="px-4 leading-relaxed md:hidden"
        v-html="shortDescription"
      >
      </div>
      <div class="px-4 md:grid md:grid-cols-2 md:gap-x-2 md:pt-12">
        <div>
          <h3 class="hidden mb-8 text-1xl md:block">
            Over het product
          </h3>
          <ProductDescription
            :description="description"
          />
        </div>
        <ProductAttributes
          :attributes="attributes"
        />
      </div>
      <ProductRelatedProducts :products="relatedProducts" />
    </div>
  </div>
</template>

<style scoped lang="scss">
.product-detail-grid {
  @apply grid grid-cols-1 grid-rows-3 grid-rows-max;
  @screen sm {
    @apply grid-cols-2 grid-rows-2 gap-x-8 gap-y-9;
  }
  grid-template-rows: auto 1fr !important;
}

.preview-icon > span {
  font-size: 14px;
}
.preview-button {
  line-height: 12px;
}
.product-title {
  @apply mb-4;
  font-size: 42px;

  @screen md {
    font-size: 52px;
  }
}
.product-price {
  text-align: left !important;
}
</style>
